import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Clock, Linkedin, Twitter } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await axios.get(`${API}/contact`);
        setContactInfo(response.data);
      } catch (error) {
        console.error('Error fetching contact info:', error);
      }
    };
    fetchContact();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg"
            alt="Contact"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a] via-[#0a0f1a]/95 to-[#0a0f1a]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
            Contact Us
          </span>
          <h1 className="heading-1 mb-6">Get In Touch</h1>
          <p className="body-text max-w-2xl mx-auto">
            Ready to transform your business? Let's discuss how we can help you 
            find the right talent or build the perfect solution.
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="section-padding" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="service-card" data-testid="contact-card-visit">
              <div className="icon-container mb-4">
                <MapPin size={20} />
              </div>
              <h3 className="font-semibold text-white mb-2">UK Office</h3>
              <p className="text-slate-500 text-sm">
                3 Thursfield Road<br />
                Tipton, DY48TJ<br />
                United Kingdom
              </p>
            </div>

            <div className="service-card" data-testid="contact-card-india">
              <div className="icon-container mb-4">
                <MapPin size={20} />
              </div>
              <h3 className="font-semibold text-white mb-2">India Office</h3>
              <p className="text-slate-500 text-sm">
                Sharebox Technologies Pvt. Ltd.<br />
                Office no 403,<br />
                Swapnali Apartments,<br />
                Near City Pride Theater, Kothrud,<br />
                Pune, Maharashtra 411038, India
              </p>
            </div>

            <div className="service-card" data-testid="contact-card-call">
              <div className="icon-container mb-4">
                <Phone size={20} />
              </div>
              <h3 className="font-semibold text-white mb-2">Call Us</h3>
              <p className="text-slate-500 text-sm">
                <a href="tel:+447438382880" className="hover:text-[#0088CC] transition-colors">
                  +44-7438382880
                </a>
              </p>
            </div>
          </div>

          {/* Contact Info Row 2 */}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div className="service-card" data-testid="contact-card-email">
              <div className="icon-container mb-4">
                <Mail size={20} />
              </div>
              <h3 className="font-semibold text-white mb-2">Email Us</h3>
              <p className="text-slate-500 text-sm">
                <a href="mailto:hello@shareboxtech.com" className="hover:text-[#0088CC] transition-colors">
                  hello@shareboxtech.com
                </a>
                <br />
                <a href="mailto:careers@shareboxtech.com" className="hover:text-[#0088CC] transition-colors">
                  careers@shareboxtech.com
                </a>
                <br />
                <a href="mailto:sales@shareboxtech.com" className="hover:text-[#0088CC] transition-colors">
                  sales@shareboxtech.com
                </a>
              </p>
            </div>

            <div className="service-card" data-testid="contact-card-hours">
              <div className="icon-container mb-4">
                <Clock size={20} />
              </div>
              <h3 className="font-semibold text-white mb-2">Business Hours</h3>
              <p className="text-slate-500 text-sm">
                Monday - Friday<br />
                9:00 AM - 6:00 PM (GMT)<br />
                Weekend: Closed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social & Map */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="contact-additional">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Social Links */}
            <div>
              <h3 className="heading-3 mb-6">Connect With Us</h3>
              <p className="text-slate-400 mb-6">
                Follow us on social media for the latest news and opportunities.
              </p>
              <div className="flex gap-4">
                <a 
                  href="https://linkedin.com/company/sharebox-technologies" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-[#111827] border border-[#1e293b] flex items-center justify-center text-slate-400 hover:text-[#0088CC] hover:border-[#0088CC]/50 transition-all"
                  data-testid="social-linkedin"
                >
                  <Linkedin size={22} />
                </a>
                <a 
                  href="https://twitter.com/shareboxtech" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-[#111827] border border-[#1e293b] flex items-center justify-center text-slate-400 hover:text-[#0088CC] hover:border-[#0088CC]/50 transition-all"
                  data-testid="social-twitter"
                >
                  <Twitter size={22} />
                </a>
              </div>

              <div className="mt-8 service-card">
                <h4 className="font-semibold text-white mb-4">Department Contacts</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">General:</span>
                    <a href="mailto:hello@shareboxtech.com" className="text-[#0088CC]">hello@shareboxtech.com</a>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Recruitment:</span>
                    <a href="mailto:recruitment@shareboxtech.com" className="text-[#0088CC]">recruitment@shareboxtech.com</a>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Projects:</span>
                    <a href="mailto:projects@shareboxtech.com" className="text-[#0088CC]">projects@shareboxtech.com</a>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Careers:</span>
                    <a href="mailto:careers@shareboxtech.com" className="text-[#0088CC]">careers@shareboxtech.com</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden h-80 lg:h-auto">
              <iframe
                title="Sharebox Technologies Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2483.542756867826!2d-0.12775892327396988!3d51.50735127181451!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487604ce176ac979%3A0x42af85654e23a0b4!2sLondon%2C%20UK!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '320px' }}
                allowFullScreen=""
                loading="lazy"
                className="grayscale contrast-125"
                data-testid="google-map"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
